import type { GraphDocument } from '@/contracts/funnel';
import { isConflictErrorCode, isRetryableErrorCode, retryDelayForAttempt } from './retry';
import { transition, type SaveState } from './save-state';

export interface SavePayload {
  expectedRevision: number;
  graph: GraphDocument;
}
export interface SaveResult {
  revision: number;
  savedAt: string;
}
export interface SaveFailure {
  code?: string;
  message: string;
}

export type SaveStateInfo = {
  message?: string;
};

export interface AutosaveCoordinatorOptions {
  save: (payload: SavePayload, signal?: AbortSignal) => Promise<SaveResult>;
  getGraphSnapshot: () => GraphDocument;
  initialRevision: number;
  graphDebounceMs?: number;
  viewportDebounceMs?: number;
  timeoutMs?: number;
  isOnline?: () => boolean;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  onStateChange?: (state: SaveState, info: SaveStateInfo) => void;
  onSaveSuccess?: (result: SaveResult & { graph: GraphDocument }) => void;
}

const DEFAULT_GRAPH_DEBOUNCE_MS = 800;
const DEFAULT_VIEWPORT_DEBOUNCE_MS = 1500;
const DEFAULT_TIMEOUT_MS = 12_000;

/**
 * SPEC-007 §31/plan §6-9 — the single module owning debounce, single-flight,
 * mutation-version protection, retry/backoff, timeout and save-state
 * transitions (T018). Nothing outside this class schedules a save timer.
 */
export class AutosaveCoordinator {
  private state: SaveState = 'clean';
  private mutationVersion = 0;
  private currentRevision: number;
  private pendingKind: 'graph' | 'viewport' | null = null;
  private timerHandle: unknown = null;
  private inFlight = false;
  private retryAttempt = 0;
  private retryTimerHandle: unknown = null;
  private conflicted = false;
  private lastError: string | undefined;
  private flushWaiters: Array<() => void> = [];

  private readonly graphDebounceMs: number;
  private readonly viewportDebounceMs: number;
  private readonly timeoutMs: number;
  private readonly isOnline: () => boolean;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly options: AutosaveCoordinatorOptions) {
    this.currentRevision = options.initialRevision;
    this.graphDebounceMs = options.graphDebounceMs ?? DEFAULT_GRAPH_DEBOUNCE_MS;
    this.viewportDebounceMs = options.viewportDebounceMs ?? DEFAULT_VIEWPORT_DEBOUNCE_MS;
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.isOnline = options.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle as number));
  }

  getState(): SaveState {
    return this.state;
  }

  getRevision(): number {
    return this.currentRevision;
  }

  private setStateDirectly(state: SaveState): void {
    this.state = state;
    this.options.onStateChange?.(state, { message: this.lastError });
  }

  private setState(next: SaveState): void {
    if (next === this.state) return;
    this.setStateDirectly(next);
  }

  private notifyMutation(kind: 'graph' | 'viewport'): void {
    this.mutationVersion += 1;
    this.setState(transition(this.state, { type: 'mutate' }));

    if (!this.isOnline()) {
      this.setState(transition(this.state, { type: 'go_offline' }));
    }

    if (this.conflicted) return; // SAVE-REQ-015: no autosave scheduling while conflicted.

    if (this.pendingKind === 'graph' && this.timerHandle !== null) {
      // A graph save is already scheduled and covers viewport too; a
      // viewport-only mutation must not downgrade or extend that deadline.
      return;
    }
    this.pendingKind = kind;
    this.rescheduleTimer(kind === 'graph' ? this.graphDebounceMs : this.viewportDebounceMs);
  }

  notifyGraphMutation(): void {
    this.notifyMutation('graph');
  }

  notifyViewportMutation(): void {
    this.notifyMutation('viewport');
  }

  private rescheduleTimer(delayMs: number): void {
    if (this.timerHandle !== null) this.clearTimer(this.timerHandle);
    this.timerHandle = this.setTimer(() => {
      this.timerHandle = null;
      void this.performSave();
    }, delayMs);
  }

  goOnline(): void {
    if (this.state !== 'offline') return;
    this.setState(transition(this.state, { type: 'go_online' }));
    this.rescheduleTimer(0);
  }

  goOffline(): void {
    this.setState(transition(this.state, { type: 'go_offline' }));
  }

  /** Cancels debounce and waits for the newest state to be safely saved (T056/navigation guard). */
  async flush(): Promise<boolean> {
    if (this.timerHandle !== null) {
      this.clearTimer(this.timerHandle);
      this.timerHandle = null;
      this.pendingKind = this.pendingKind ?? 'graph';
    }
    if (this.state === 'clean' || this.state === 'saved') return true;
    if (this.conflicted) return false;
    if (!this.isOnline()) return false;

    if (this.inFlight) {
      await new Promise<void>((resolve) => this.flushWaiters.push(resolve));
    } else {
      await this.performSave();
    }
    const finalState = this.getState();
    return finalState === 'clean' || finalState === 'saved';
  }

  private clearRetryTimer(): void {
    if (this.retryTimerHandle !== null) {
      this.clearTimer(this.retryTimerHandle);
      this.retryTimerHandle = null;
    }
  }

  retry(): void {
    if (this.state !== 'error') return;
    this.retryAttempt = 0;
    void this.performSave();
  }

  private async performSave(): Promise<void> {
    if (this.inFlight || this.conflicted) return;
    if (!this.isOnline()) {
      this.setState('offline');
      return;
    }

    this.inFlight = true;
    this.pendingKind = null;
    const saveMutationVersion = this.mutationVersion;
    this.setState(transition(this.state, { type: 'save_start' }));

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    let timeoutHandle: unknown;
    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      timeoutHandle = this.setTimer(() => {
        controller?.abort();
        reject({ message: 'Save timed out.' });
      }, this.timeoutMs);
    });

    try {
      const graph = this.options.getGraphSnapshot();
      const result = await Promise.race([
        this.options.save({ expectedRevision: this.currentRevision, graph }, controller?.signal),
        timeoutPromise,
      ]);
      this.clearTimer(timeoutHandle);
      this.currentRevision = result.revision;
      this.retryAttempt = 0;
      const hasNewerChange = this.mutationVersion !== saveMutationVersion;
      this.setState(transition(this.state, { type: 'save_success', hasNewerChange }));
      this.options.onSaveSuccess?.({ ...result, graph });

      if (hasNewerChange) {
        this.rescheduleTimer(this.graphDebounceMs);
      } else {
        this.setTimer(() => this.setState(transition(this.state, { type: 'settle' })), 1200);
      }
    } catch (error) {
      this.clearTimer(timeoutHandle);
      const failure = normalizeFailure(error);
      this.lastError = failure.message;

      if (isConflictErrorCode(failure.code)) {
        this.conflicted = true;
        this.setState('conflict');
      } else if (!isRetryableErrorCode(failure.code)) {
        this.setState('error');
      } else {
        const delay = retryDelayForAttempt(this.retryAttempt);
        if (delay === null) {
          this.setState('error');
        } else {
          this.retryAttempt += 1;
          this.retryTimerHandle = this.setTimer(() => {
            this.retryTimerHandle = null;
            void this.performSave();
          }, delay);
        }
      }
    } finally {
      this.inFlight = false;
      const waiters = this.flushWaiters;
      this.flushWaiters = [];
      for (const resolve of waiters) resolve();
    }
  }

  /** Called after a user explicitly reloads the server graph on conflict (SAVE-REQ-015). */
  acknowledgeReload(newRevision: number): void {
    this.conflicted = false;
    this.retryAttempt = 0;
    this.clearRetryTimer();
    this.currentRevision = newRevision;
    this.mutationVersion = 0;
    this.setStateDirectly('clean');
  }

  /** SAVE-REQ-015 "Keep local recovery": stays dirty/conflict-free but does not autosave over the server. */
  keepLocalWithoutOverwrite(): void {
    // Deliberately a no-op beyond leaving `conflicted` untouched: the
    // caller is expected to have already written a recovery record; the
    // coordinator must not schedule a save while still conflicted.
  }

  dispose(): void {
    if (this.timerHandle !== null) this.clearTimer(this.timerHandle);
    this.clearRetryTimer();
  }
}

function normalizeFailure(error: unknown): SaveFailure {
  if (error && typeof error === 'object' && 'code' in error) {
    const withCode = error as { code?: unknown; message?: unknown };
    return {
      code: typeof withCode.code === 'string' ? withCode.code : undefined,
      message: typeof withCode.message === 'string' ? withCode.message : 'Save failed.',
    };
  }
  if (error instanceof Error) return { message: error.message };
  return { message: 'Save failed.' };
}
